import { useAuthContext } from '@invisoutlet/shared/context';
import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Sensor network data for the atmospheric left panel ───────────────────────
// Nodes represent InvisOutlet devices scattered across a building network.
// Labels show live sensor readings as a visual metaphor — decorative only.

const NODES = [
  { x: 12, y: 18, delay: 0.0, label: '69°F' },
  { x: 30, y: 9, delay: 0.7, label: null },
  { x: 48, y: 21, delay: 1.4, label: '45%' },
  { x: 70, y: 13, delay: 0.3, label: null },
  { x: 87, y: 29, delay: 1.9, label: 'ON' },
  { x: 18, y: 44, delay: 1.1, label: null },
  { x: 46, y: 51, delay: 0.5, label: '67°F' },
  { x: 73, y: 56, delay: 1.7, label: null },
  { x: 90, y: 69, delay: 0.1, label: 'Occupied' },
  { x: 14, y: 73, delay: 1.3, label: null },
  { x: 38, y: 81, delay: 0.8, label: '51%' },
  { x: 63, y: 86, delay: 1.6, label: null },
] as const;

const EDGES: readonly [number, number][] = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [0, 5],
  [5, 6],
  [6, 7],
  [7, 8],
  [1, 5],
  [2, 6],
  [3, 7],
  [5, 9],
  [9, 10],
  [10, 11],
  [6, 10],
  [7, 11],
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function LoginPage() {
  const { user, loading, error: authError, login } = useAuthContext();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Login failed. Please check your credentials.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  const displayError = formError ?? authError;

  return (
    <>
      {/* Page-scoped keyframe animations — cannot be expressed in Tailwind without config */}
      <style>{`
        @keyframes node-pulse {
          0%, 100% { opacity: 0.25; }
          50%       { opacity: 0.9; }
        }
        @keyframes edge-pulse {
          0%, 100% { opacity: 0.05; }
          50%       { opacity: 0.2; }
        }
        @keyframes form-enter {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes dot-breathe {
          0%, 100% { transform: scale(1);   opacity: 0.6; }
          50%       { transform: scale(1.5); opacity: 1; }
        }
      `}</style>

      <div className="relative flex min-h-screen overflow-hidden" style={{ background: '#040d1c' }}>
        {/* ── Left atmospheric panel ────────────────────────────────────────── */}
        <div
          className="relative hidden flex-col lg:flex"
          style={{
            width: '60%',
            borderRight: '1px solid rgba(59,130,246,0.12)',
            background: 'linear-gradient(135deg, #040d1c 0%, #071220 50%, #040a16 100%)',
          }}
        >
          {/* Ambient radial glow — top right */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 55% 45% at 80% 10%, rgba(59,130,246,0.08) 0%, transparent 70%),' +
                'radial-gradient(ellipse 40% 35% at 15% 85%, rgba(59,130,246,0.05) 0%, transparent 65%)',
            }}
          />

          {/* Sensor network SVG — fills the entire panel */}
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid slice"
          >
            {/* Connecting edges */}
            {EDGES.map(([a, b], i) => (
              <line
                key={`${a}-${b}`}
                x1={NODES[a].x}
                y1={NODES[a].y}
                x2={NODES[b].x}
                y2={NODES[b].y}
                stroke="#3b82f6"
                strokeWidth="0.18"
                style={{
                  animation: `edge-pulse ${3.5 + (i % 3) * 0.6}s ease-in-out infinite`,
                  animationDelay: `${(NODES[a].delay + NODES[b].delay) / 2}s`,
                }}
              />
            ))}

            {/* Nodes */}
            {NODES.map((node) => (
              <g key={`${node.x}-${node.y}`}>
                {/* Outer ring (pulse) */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="1.6"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="0.15"
                  style={{
                    animation: `node-pulse ${2.8 + node.delay * 0.4}s ease-in-out infinite`,
                    animationDelay: `${node.delay}s`,
                  }}
                />
                {/* Core dot */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="0.55"
                  fill="#3b82f6"
                  style={{
                    animation: `node-pulse ${2.8 + node.delay * 0.4}s ease-in-out infinite`,
                    animationDelay: `${node.delay}s`,
                  }}
                />
                {/* Sensor label */}
                {node.label && (
                  <text
                    x={node.x + 2}
                    y={node.y - 1.4}
                    fontSize="2.2"
                    fill="rgba(148,163,184,0.55)"
                    fontFamily="ui-monospace, monospace"
                    style={{
                      animation: `node-pulse ${2.8 + node.delay * 0.4}s ease-in-out infinite`,
                      animationDelay: `${node.delay + 0.2}s`,
                    }}
                  >
                    {node.label}
                  </text>
                )}
              </g>
            ))}
          </svg>

          {/* Panel content — layered above the SVG */}
          <div className="relative flex h-full flex-col justify-between p-12">
            {/* Top: logo + brand */}
            <div className="flex items-center gap-3">
              <img
                src="/favicon.png"
                alt="Intecular"
                className="h-8 w-8 rounded-lg"
                draggable={false}
              />
              <span className="text-sm font-semibold tracking-wide" style={{ color: '#94a3b8' }}>
                InvisOutlet by Intecular
              </span>
            </div>

            {/* Center: main statement */}
            <div>
              <h1
                className="text-[2.75rem] font-bold leading-[1.1] tracking-tight"
                style={{ color: '#f8fafc' }}
              >
                Monitor every outlet.
                <br />
                Every building.
                <br />
                <span style={{ color: '#3b82f6' }}>In real time.</span>
              </h1>
              <p className="mt-5 max-w-xs text-base leading-relaxed" style={{ color: '#64748b' }}>
                Real-time IoT monitoring and control for property managers — temperature, humidity,
                occupancy, and outlet state across every floor.
              </p>
            </div>

            {/* Bottom: live stats */}
            <div
              className="flex items-center gap-6 text-xs font-medium tracking-wide"
              style={{ color: '#475569' }}
            >
              {/* {[
                { value: '24', label: 'Buildings' },
                { value: '1,240', label: 'Devices' },
                { value: '99.8%', label: 'Uptime' },
              ].map(({ value, label }, i) => (
                <div key={label} className="flex items-center gap-6">
                  {i > 0 && (
                    <div
                      className="h-3 w-px"
                      style={{ background: 'rgba(71,85,105,0.5)' }}
                      aria-hidden="true"
                    />
                  )}
                  <div className="flex items-baseline gap-1.5">
                    <span
                      className="text-sm font-semibold"
                      style={{ color: '#94a3b8' }}
                    >
                      {value}
                    </span>
                    <span>{label}</span>
                  </div>
                </div>
              ))} */}

              {/* Live indicator */}
              <div className="ml-auto flex items-center gap-2">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    background: '#22c55e',
                    animation: 'dot-breathe 2s ease-in-out infinite',
                  }}
                  aria-hidden="true"
                />
                <span style={{ color: '#22c55e' }}>Live</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right form panel ─────────────────────────────────────────────── */}
        <div
          className="flex flex-1 items-center justify-center px-8 py-12"
          style={{ background: '#040d1c' }}
        >
          <div
            className="w-full max-w-sm"
            style={{
              animation: 'form-enter 0.5s cubic-bezier(0.16,1,0.3,1) both',
              animationDelay: '0.1s',
            }}
          >
            {/* Mobile-only logo */}
            <div className="mb-10 flex items-center gap-3 lg:hidden">
              <img
                src="/favicon.png"
                alt="Intecular"
                className="h-8 w-8 rounded-lg"
                draggable={false}
              />
              <span className="text-sm font-semibold tracking-wide" style={{ color: '#94a3b8' }}>
                InvisOutlet by Intecular
              </span>
            </div>

            {/* Heading */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: '#f8fafc' }}>
                Welcome back
              </h2>
              <p className="mt-1 text-sm" style={{ color: '#64748b' }}>
                Sign in to your workspace
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="email"
                  className="text-xs font-semibold uppercase tracking-widest"
                  style={{ color: '#64748b' }}
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-lg px-4 py-3 text-sm outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#f8fafc',
                    caretColor: '#3b82f6',
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid rgba(59,130,246,0.7)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid rgba(255,255,255,0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="password"
                  className="text-xs font-semibold uppercase tracking-widest"
                  style={{ color: '#64748b' }}
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-lg px-4 py-3 text-sm outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#f8fafc',
                    caretColor: '#3b82f6',
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid rgba(59,130,246,0.7)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid rgba(255,255,255,0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Error */}
              {displayError && (
                <div
                  role="alert"
                  className="rounded-lg px-4 py-3 text-sm"
                  style={{
                    background: 'rgba(220,38,38,0.1)',
                    border: '1px solid rgba(220,38,38,0.25)',
                    color: '#fca5a5',
                  }}
                >
                  {displayError}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || loading}
                className="mt-1 rounded-lg py-3 text-sm font-semibold text-white transition-all focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                style={{
                  background: submitting ? 'rgba(59,130,246,0.7)' : '#2563eb',
                  boxShadow: submitting ? 'none' : '0 0 24px rgba(59,130,246,0.25)',
                }}
                onMouseEnter={(e) => {
                  if (!submitting) (e.target as HTMLButtonElement).style.background = '#1d4ed8';
                }}
                onMouseLeave={(e) => {
                  if (!submitting) (e.target as HTMLButtonElement).style.background = '#2563eb';
                }}
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="mt-8 text-center text-xs" style={{ color: '#334155' }}>
              Need access? <span style={{ color: '#475569' }}>Contact your administrator.</span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
